"use client";

import { useState, useEffect } from "react";
import styles from "../../../styles/patient-page-modules/Charts.module.css";
import Table from "../../table";
import StatusModal from "../modal/StatusModal";
import DecisionModal from "../modal/DecisionModal";
import EscalationModal from "../modal/EscalationModal";
import ReviewModal from "../modal/ReviewModal";

import { updateReadingStatus } from "../../../services/admin-case-manager/readings";
import { convertUTCToLocal } from "../../../utils/helper";

const Charts = ({
  showActionButtons = false,
  readings = [],
  patientBounds,
  onStatusChange,
  onRefresh,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [localReadings, setLocalReadings] = useState(readings);
  const [isUpdating, setIsUpdating] = useState(false);

  const [decisionOpen, setDecisionOpen] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({
    title: "",
    message: "",
    status: "",
    isLoading: false,
  });

  useEffect(() => {
    setLocalReadings(readings);
  }, [readings]);
 

  const handleSelectionChange = (ids) => {
    setSelectedIds(ids);
  };

  const closeStatusModal = () => setModalOpen(false);

  const showLoadingModal = (title, message) => {
    setModalProps({ title, message, status: "", isLoading: true });
    setModalOpen(true);
  };

  const updateModalStatus = (status, message) => {
    setModalProps((prev) => ({
      ...prev,
      status,
      message,
      isLoading: false,
    }));
  };

  const compareValue = (value, low, high) => {
    if (value > high) {
      const diff = Math.round(value - high);
      return {
        state: "high",
        text: `↑ ${diff} above upper bound`,
      };
    }

    if (value < low) {
      const diff = Math.round(low - value);
      return {
        state: "low",
        text: `↓ ${diff} below lower bound`,
      };
    }

    return {
      state: "normal",
      text: "Within range",
    };
  };

  const getComparisonData = (r) => {
    if (!patientBounds) return null;

    switch (r.reading_type) {
      case "blood_pressure":
        return [
          {
            label: "Systolic",
            unit: "mmHg",
            bounds: `${patientBounds.systolic_low}–${patientBounds.systolic_high}`,
            ...compareValue(
              r.systolic,
              patientBounds.systolic_low,
              patientBounds.systolic_high
            ),
          },
          {
            label: "Diastolic",
            unit: "mmHg",
            bounds: `${patientBounds.diastolic_low}–${patientBounds.diastolic_high}`,
            ...compareValue(
              r.diastolic,
              patientBounds.diastolic_low,
              patientBounds.diastolic_high
            ),
          },
        ];

      case "blood_glucose":
        return [
          {
            label: "Glucose",
            unit: "mg/dL",
            bounds: `${patientBounds.glucose_low}–${patientBounds.glucose_high}`,
            ...compareValue(
              r.glucose,
              patientBounds.glucose_low,
              patientBounds.glucose_high
            ),
          },
        ];

      case "temperature":
        return [
          {
            label: "Temperature",
            unit: "°C",
            bounds: `${patientBounds.temperature_low}–${patientBounds.temperature_high}`,
            ...compareValue(
              r.temperature,
              patientBounds.temperature_low,
              patientBounds.temperature_high
            ),
          },
        ];

      case "pulse_ox":
        return [
          {
            label: "Oxygen",
            unit: "%",
            bounds: `${patientBounds.oxygen_low}–${patientBounds.oxygen_high}`,
            ...compareValue(
              r.oxygen,
              patientBounds.oxygen_low,
              patientBounds.oxygen_high
            ),
          },
        ];

      case "weight":
        return [
          {
            label: "Weight",
            unit: "lbs",
            bounds: `${patientBounds.weight_low}–${patientBounds.weight_high}`,
            ...compareValue(
              r.weight,
              patientBounds.weight_low,
              patientBounds.weight_high
            ),
          },
        ];

      default:
        return null;
    }
  };

  const renderComparisonCell = (r) => {
    const items = getComparisonData(r);
    if (!items) return "—";

    return (
      <div className={styles.compareCell}>
        {items.map((i) => (
          <div key={i.label} className={styles.compareItem}>
            <div
              className={
                i.state === "high"
                  ? styles.high
                  : i.state === "low"
                  ? styles.low
                  : styles.normal
              }
            >
              {i.text}
            </div>
            <div className={styles.bounds}>
              {i.label}: {i.bounds} {i.unit}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getCategoriesForReading = (r) => {
    const cats = [];

    if (r.category) cats.push(r.category);
    if (r.bp_category) cats.push(`BP: ${r.bp_category}`);
    if (r.heart_rate_category) cats.push(`HR: ${r.heart_rate_category}`);

    if (r.glucose && r.reading_type === "blood_glucose" && !r.category) {
      if (r.glucose >= 300) cats.push("Diabetes");
      else if (r.glucose >= 140) cats.push("Prediabetes");
    }

    if (r.oxygen && r.reading_type === "pulse_ox" && !r.category) {
      if (r.oxygen < 90) cats.push("Hypoxia");
      else cats.push("Normal");
    }

    if (r.heart_rate && r.reading_type === "heart_rate" && r.heart_rate_category) {
      cats.push(`HR: ${r.heart_rate_category}`);
    }

    return Array.from(new Set(cats.filter(Boolean)));
  };

  const renderCategoryCell = (r) => {
    const cats = getCategoriesForReading(r);
    if (!cats || cats.length === 0) return "—";
    return (
      <div className={styles.categoryCell}>
        {cats.map((c, i) => (
          <div key={i} className={styles.categoryItem}>
            <span className={styles.catBullet}>•</span>
            <span className={styles.catText}>{c}</span>
          </div>
        ))}
      </div>
    );
  };

  const getSelectedCategoriesText = () => {
    if (!selectedIds || selectedIds.length === 0) return "";
    const selectedReadings = localReadings.filter((r) => selectedIds.includes(r.id));
    const allCats = selectedReadings.flatMap((r) => getCategoriesForReading(r));
    const unique = Array.from(new Set(allCats.filter(Boolean)));
    return unique.join(", ");
  };

  const handleStatusSubmit = async (payload, config) => {
    setIsUpdating(true);
    showLoadingModal(config.loadingTitle, config.loadingMessage);

    try {
      if (selectedIds.length) {
        const patientId = sessionStorage.getItem("_pt_id");
        if (!patientId) {
          throw new Error("Patient ID not found");
        }

        const requestPayload = {
          readings: selectedIds,
          escalated: config.escalated,
          reviewed: config.reviewed,
          note: payload.note || "",
          follow_up_seconds: config.getFollowUpSeconds(payload) || 0,
        };

        const result = await updateReadingStatus(patientId, requestPayload);

        if (!result.success) {
          throw new Error(result.error || config.errorMessage);
        }

        setLocalReadings((prev) =>
          prev.map((r) =>
            selectedIds.includes(r.id)
              ? config.updateReading(r, payload)
              : r
          )
        );
      }

      updateModalStatus("Success", config.successMessage);

      setSelectedIds([]);
      config.closeModal();

      if (typeof onStatusChange === "function") onStatusChange();
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      console.error(err);
      updateModalStatus("Error", config.errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEscalationSubmit = async (payload) => {
    handleStatusSubmit(payload, {
      loadingTitle: "Submitting escalation",
      loadingMessage: "Please wait while escalation is submitted...",
      escalated: true,
      reviewed: false,
      getFollowUpSeconds: (p) => p.followUpTimeMinutes || 0,
      updateReading: (r, p) => ({
        ...r,
        reviewed: false,
        esclated: true,
        notes: p.note || r.notes,
        escalation_category: p.category || getSelectedCategoriesText(),
        escalation_follow_up_minutes: p.followUpTimeMinutes || 0,
      }),
      successMessage: "Escalation submitted.",
      errorMessage: "Failed to submit escalation.",
      closeModal: () => setEscalationOpen(false),
    });
  };

  const handleReviewSubmit = async (payload) => {
    handleStatusSubmit(payload, {
      loadingTitle: "Submitting review",
      loadingMessage: "Please wait while review is submitted...",
      escalated: false,
      reviewed: true,
      getFollowUpSeconds: (p) => p.timeSpentMinutes || 0,
      updateReading: (r, p) => ({
        ...r,
        reviewed: true,
        esclated: false,
        notes: p.note || r.notes,
      }),
      successMessage: "Review submitted.",
      errorMessage: "Failed to submit review.",
      closeModal: () => setReviewOpen(false),
    });
  };

  const columns = [
    {
      key: "recorded_at",
      label: "Date",
      alignLeft: true,
      render: (r) => convertUTCToLocal(r.recorded_at),
    },
    {
      key: "type",
      label: "Type",
      alignLeft: true,
      render: (r) => {
        const map = {
          blood_pressure: "Blood Pressure",
          weight: "Weight",
          temperature: "Temperature",
          pulse_ox: "Pulse Oximeter",
          blood_glucose: "Blood Glucose",
        };
        return map[r.reading_type] || "—";
      },
    },
  
    {
      key: "value",
      label: "Value",
      alignLeft: true,
      render: (r) => {
        switch (r.reading_type) {
          case "blood_pressure":
            return (
              <>
                <p>
                  {r.systolic} / {r.diastolic} mmHg
                </p>
                <p>{r.heart_rate} bpm</p>
              </>
            );
          case "weight":
            return <p>{r.weight} lbs</p>;
          case "temperature":
            return <p>{r.temperature} °C</p>;
          case "pulse_ox":
            return (
              <>
                <p>{r.oxygen}%</p>
                <p>{r.heart_rate} bpm</p>
              </>
            );
          case "blood_glucose":
            return <p>{r.glucose} mg/dL</p>;
          default:
            return "—";
        }
      },
    },
    {
      key: "comparison",
      label: "Comparison",
      alignLeft: true,
      sorting: false,
      render: (r) => renderComparisonCell(r),
    },
      {
      key: "category",
      label: "Category",
      alignLeft: true,
      sorting: false,
      render: (r) => renderCategoryCell(r),
    },
  ];

  return (
    <div className={styles.bp}>
      <Table
        columns={columns}
        data={localReadings}
        height={330}
        showCheckboxes
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
      />

      {showActionButtons && (
        <div className={styles.actionButtons}>
          <button
            className={styles.escalateButton}
            disabled={!selectedIds.length || isUpdating}
            onClick={() => setDecisionOpen(true)}
          >
            Escalate / Review
          </button>
        </div>
      )}

      {}
      <DecisionModal
        isOpen={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        onEscalate={() => {
          setDecisionOpen(false);
          setEscalationOpen(true);
        }}
        onReview={() => {
          setDecisionOpen(false);
          setReviewOpen(true);
        }}
      />

      {}
      <EscalationModal
        isOpen={escalationOpen}
        onClose={() => setEscalationOpen(false)}
        categoryText={getSelectedCategoriesText()}
        onSubmit={handleEscalationSubmit}
      />

      <ReviewModal
        isOpen={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onSubmit={handleReviewSubmit}
      />

      <StatusModal
        isOpen={modalOpen}
        onClose={closeStatusModal}
        title={modalProps.title}
        message={modalProps.message}
        status={modalProps.status}
        isLoading={modalProps.isLoading}
      />
    </div>
  );
};

export default Charts;

